import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// third party
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import ChangePasswordDialog from './ChangePasswordDialog';

// assets
import { IconKey, IconLogout, IconSettings } from '@tabler/icons-react';

function greetingId(): string {
  const h = new Date().getHours();
  if (h < 10) return 'profile.greeting.morning';
  if (h < 18) return 'profile.greeting.day';
  return 'profile.greeting.evening';
}

function userInitials(user: { firstName?: string | null; lastName?: string | null; name?: string | null } | null): string {
  if (!user) return '?';
  const first = (user.firstName ?? '').trim();
  const last = (user.lastName ?? '').trim();
  if (first || last) {
    return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase() || '?';
  }
  const name = (user.name ?? '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const {
    state: { borderRadius }
  } = useConfig();

  const { logout, user } = useAuth();
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const anchorRef = useRef<any>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const initials = userInitials(user as any);
  const displayName = user?.name?.trim() || (user as any)?.firstName || user?.email || '';
  const greetingDefaults: Record<string, string> = {
    'profile.greeting.morning': 'Jó reggelt',
    'profile.greeting.day': 'Jó napot',
    'profile.greeting.evening': 'Jó estét'
  };
  const gid = greetingId();
  const greeting = intl.formatMessage({ id: gid, defaultMessage: greetingDefaults[gid] });

  return (
    <>
      <Chip
        slotProps={{ label: { sx: { lineHeight: 0 } } }}
        sx={{ ml: 2, height: '48px', alignItems: 'center', borderRadius: '27px' }}
        icon={
          <Avatar
            sx={{
              ...theme.typography.mediumAvatar,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.85rem',
              fontWeight: 600,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
          >
            {initials}
          </Avatar>
        }
        label={<IconSettings stroke={1.5} size="24px" />}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
        aria-label="Felhasználó menü"
      />
      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                    <Box sx={{ p: 2, pb: 1 }}>
                      <Stack>
                        <Typography variant="h4">
                          {greeting}
                          {displayName ? `, ${displayName}` : ''}
                        </Typography>
                        <Typography variant="subtitle2">
                          {user?.admin ? (
                            <FormattedMessage id="profile.role.admin" defaultMessage="Adminisztrátor" />
                          ) : (
                            <FormattedMessage id="profile.role.user" defaultMessage="Hotel felhasználó" />
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ px: 1, pb: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 260,
                          borderRadius: `${borderRadius}px`,
                          '& .MuiListItemButton-root': { mt: 0.5 }
                        }}
                      >
                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          onClick={() => {
                            setOpen(false);
                            setPwOpen(true);
                          }}
                        >
                          <ListItemIcon>
                            <IconKey stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="profile.changePassword" defaultMessage="Jelszó módosítása" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        <ListItemButton sx={{ borderRadius: `${borderRadius}px` }} onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="profile.logout" defaultMessage="Kijelentkezés" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}
